import type {RawStyleBlockServerType} from '@packages/systems/core/utils/StyleTypes';
import {TagStyleExport} from '../types';
import {LIBRARY_EXPORT_TYPES} from '../constants';
import {isTagStyle, type TagStyleFields} from '../tagStyles';
import {readRawTagStyleFields} from './styles';

/**
 * Predicate for whether a StyleBlock should ship as a tag style export.
 *
 * Accepts the four fields it inspects rather than a full
 * `RawStyleBlockServerType`, so it can be shared between the server publish
 * pipeline (where the block lives under `block.data.*`) and the Designer
 * Flux selectors (where the same fields are flat on an Immutable Record).
 *
 * Only a site's own tag style is exportable — a block with an `origin` already
 * belongs to a library, which is the case `isHiddenLibraryTagStyle` governs.
 */
export function isExportableTagStyle(fields: TagStyleFields): boolean {
  return isTagStyle(fields) && !fields.origin;
}

/**
 * Extracts tag StyleBlocks from a site's StyleBlocks as tag style exports.
 */
export function getTagStyleExportsFromStyleBlocks(
  styleBlocks: RawStyleBlockServerType[]
): TagStyleExport[] {
  return styleBlocks.reduce((acc: TagStyleExport[], block) => {
    if (isExportableTagStyle(readRawTagStyleFields(block))) {
      acc.push({
        type: LIBRARY_EXPORT_TYPES.TAG_STYLE,
        metadata: {
          targetStyleBlockId: block._id,
          displayName: block.data.name,
        },
      });
    }
    return acc;
  }, []);
}
